import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { ChevronRight, ArrowRight } from 'lucide-react';

/* ── Card Base Components ── */

export const Card = ({ children, onClick, style = {}, className = '' }) => {
    const { isDark } = useTheme();
    const acc = isDark ? 'rgba(245,158,11,0.35)' : 'rgba(59,130,246,0.4)';
    const bg = isDark ? 'rgba(255,255,255,0.04)' : '#ffffff';
    const bdr = isDark ? 'rgba(255,255,255,0.09)' : '#e2e8f0';

    return (
        <motion.div
            onClick={onClick}
            className={className}
            initial={false}
            whileHover={onClick ? {
                y: -2,
                borderColor: acc,
                boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.4)' : '0 8px 24px rgba(59,130,246,0.12)',
            } : {}}
            transition={{ type: 'tween', duration: 0.2 }}
            style={{
                background: bg,
                border: `1px solid ${bdr}`,
                borderRadius: 20,
                padding: 0,
                cursor: onClick ? 'pointer' : 'default',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                position: 'relative',
                ...style,
            }}
        >
            {children}
        </motion.div>
    );
};

export const CardTitle = ({ children, subtitle, icon, color, eyebrow }) => {
    const { isDark } = useTheme();
    const txtPrimary = isDark ? '#fff' : '#0f172a';
    const txtMuted = isDark ? 'rgba(255,255,255,0.4)' : '#64748b';
    return (
        <div style={{ marginBottom: 4 }}>
            {eyebrow && (
                <span style={{
                    fontSize: 10, fontWeight: 800, color: color || (isDark ? '#F59E0B' : '#2563eb'),
                    textTransform: 'uppercase', letterSpacing: '0.12em',
                    display: 'block', marginBottom: 8
                }}>
                    {eyebrow}
                </span>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                {icon && (
                    <div style={{
                        width: 32, height: 32, borderRadius: 8,
                        background: (color || (isDark ? '#F59E0B' : '#2563eb')) + '18',
                        border: `1px solid ${(color || (isDark ? '#F59E0B' : '#2563eb'))}30`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: color || (isDark ? '#F59E0B' : '#2563eb'), flexShrink: 0
                    }}>{icon}</div>
                )}
                <h3 style={{
                    fontSize: 15, fontWeight: 700, color: txtPrimary,
                    fontFamily: icon ? "'Playfair Display', serif" : "'Manrope', sans-serif",
                    lineHeight: 1.4, flex: 1, margin: 0
                }}>{children}</h3>
            </div>
            {subtitle && <p style={{ fontSize: 11, color: txtMuted, lineHeight: 1.5, margin: 0 }}>{subtitle}</p>}
        </div>
    );
};

export const CardTag = ({ label, color }) => {
    const { isDark } = useTheme();
    const tagBg = isDark ? `${color}18` : `${color}14`;
    const tagBorder = isDark ? `${color}35` : `${color}30`;
    return (
        <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            alignSelf: 'flex-start',
            width: 'fit-content',
            padding: '2px 8px',
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 700,
            lineHeight: 1.2,
            color: color,
            background: tagBg,
            border: `1px solid ${tagBorder}`,
            letterSpacing: '0.02em',
            marginBottom: 8,
        }}>{label}</span>
    );
};

export const CardIllustration = ({ children, bgGradient, height = 120 }) => {
    const { isDark } = useTheme();
    return (
        <div style={{
            width: '100%',
            height: height,
            background: isDark
                ? (bgGradient || 'linear-gradient(135deg, rgba(30,30,30,0.9), rgba(20,20,20,0.95))')
                : (bgGradient || 'linear-gradient(135deg, #f0f4f8, #e2e8f0)'),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            position: 'relative',
        }}>
            {children}
        </div>
    );
};

export const CardContent = ({ children, style = {} }) => (
    <div style={{
        padding: '16px 20px 20px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        ...style
    }}>
        {children}
    </div>
);

export const SeeMore = ({ label, onClick, color }) => {
    const { isDark } = useTheme();
    const defaultColor = isDark ? '#F59E0B' : '#2563eb';
    const c = color || defaultColor;
    const hoverColor = isDark ? '#fff' : '#1e293b';
    const divider = isDark ? 'rgba(255,255,255,0.06)' : '#e2e8f0';

    return (
        <motion.button
            onClick={(e) => { e.stopPropagation(); onClick(e); }}
            initial={false}
            whileHover={{ color: hoverColor }}
            transition={{ duration: 0.15 }}
            style={{
                marginTop: 'auto',
                paddingTop: 14,
                borderTop: `1px solid ${divider}`,
                fontSize: 12,
                fontWeight: 700,
                color: c,
                background: 'none',
                borderLeft: 'none',
                borderRight: 'none',
                borderBottom: 'none',
                cursor: 'pointer',
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                gap: 4,
                paddingBottom: 0,
            }}
        >
            {label} <ArrowRight size={13} style={{ marginLeft: 'auto' }} />
        </motion.button>
    );
};

/* ── Illustrations ── */

export const IllustrationHiringNow = () => {
    const { isDark } = useTheme();
    const coreColor = isDark ? '#10B981' : '#D04020';
    const coreLighter = isDark ? '#34D399' : '#FF6B3D';
    const ringColor = isDark ? '#10B981' : '#E86840';
    const gridLine = isDark ? 'rgba(255,255,255,0.06)' : '#F0957A';
    const refRing = isDark ? 'rgba(255,255,255,0.08)' : '#EDBAA8';
    const badgeBg = isDark ? '#dc2626' : '#C03015';
    return (
        <CardIllustration bgGradient={isDark ? 'linear-gradient(135deg, #111, #0a120e)' : 'radial-gradient(circle, #FFF4F0, #FDE8E0)'}>
            <svg width="100%" height="100%" viewBox="40 20 320 230" preserveAspectRatio="xMidYMid meet">
                <defs>
                    <style>{`
                        @keyframes pulse1{0%,100%{r:36;opacity:.18}50%{r:60;opacity:0}}
                        @keyframes pulse2{0%,100%{r:24;opacity:.28}50%{r:48;opacity:.05}}
                        @keyframes pulse3{0%,100%{r:14;opacity:.38}50%{r:38;opacity:.1}}
                        @keyframes badgePulse{0%,100%{opacity:1}50%{opacity:.7}}
                        .ci-r1{animation:pulse1 2.4s ease-in-out infinite}
                        .ci-r2{animation:pulse2 2.4s ease-in-out infinite .4s}
                        .ci-r3{animation:pulse3 2.4s ease-in-out infinite .8s}
                        .ci-bp{animation:badgePulse 2s ease-in-out infinite}
                    `}</style>
                </defs>
                <line x1="200" y1="20" x2="200" y2="240" stroke={gridLine} strokeWidth="0.5" opacity="0.3" />
                <line x1="40" y1="140" x2="360" y2="140" stroke={gridLine} strokeWidth="0.5" opacity="0.3" />
                <circle cx="200" cy="140" r="100" fill="none" stroke={refRing} strokeWidth="0.5" opacity="0.5" />
                <circle cx="200" cy="140" r="70" fill="none" stroke={refRing} strokeWidth="0.5" opacity="0.5" />
                <circle cx="200" cy="140" r="40" fill="none" stroke={refRing} strokeWidth="0.5" opacity="0.5" />
                <circle className="ci-r1" cx="200" cy="140" r="36" fill={ringColor} opacity="0.18" />
                <circle className="ci-r2" cx="200" cy="140" r="24" fill={ringColor} opacity="0.28" />
                <circle className="ci-r3" cx="200" cy="140" r="14" fill={ringColor} opacity="0.38" />
                <circle cx="200" cy="140" r="20" fill={coreColor} />
                <circle cx="200" cy="140" r="9" fill={coreLighter} />
                <circle cx="197" cy="137" r="3" fill="white" opacity="0.5" />
                <rect x="218" y="118" width="46" height="20" rx="10" fill={badgeBg} />
                <text className="ci-bp" x="241" y="132" textAnchor="middle" fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize="10" fontWeight="700" fill="white" letterSpacing="1">LIVE</text>
            </svg>
        </CardIllustration>
    );
};

export const IllustrationReferral = () => {
    const { isDark } = useTheme();
    const nodeMain = isDark ? '#10B981' : '#3B9A54';
    const nodeOther = isDark ? '#059669' : '#4CAF60';
    const nodeHighlight = isDark ? '#34D399' : '#7DD48A';
    const lineColor = isDark ? '#34D399' : '#6BBD7E';
    const outerRing = isDark ? '#065F46' : '#CCEBBF';
    const badgeBg = isDark ? '#059669' : '#2E7D40';
    return (
        <CardIllustration bgGradient={isDark ? 'linear-gradient(135deg, #111, #0a110d)' : 'radial-gradient(circle, #F4FAF0, #E6F3DC)'}>
            <svg width="100%" height="100%" viewBox="50 30 300 240" preserveAspectRatio="xMidYMid meet">
                <defs>
                    <style>{`
                        @keyframes nodeApp{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.8;transform:scale(1.06)}}
                        .ci-n1{animation:nodeApp 3s ease-in-out infinite 0s;transform-origin:200px 145px}
                        .ci-n2{animation:nodeApp 3s ease-in-out infinite .5s;transform-origin:130px 100px}
                        .ci-n3{animation:nodeApp 3s ease-in-out infinite 1s;transform-origin:270px 100px}
                        .ci-n4{animation:nodeApp 3s ease-in-out infinite 1.5s;transform-origin:150px 195px}
                        .ci-n5{animation:nodeApp 3s ease-in-out infinite 2s;transform-origin:250px 195px}
                    `}</style>
                </defs>
                <line x1="200" y1="145" x2="130" y2="100" stroke={lineColor} strokeWidth="1.5" opacity="0.4" />
                <line x1="200" y1="145" x2="270" y2="100" stroke={lineColor} strokeWidth="1.5" opacity="0.4" />
                <line x1="200" y1="145" x2="150" y2="195" stroke={lineColor} strokeWidth="1.5" opacity="0.4" />
                <line x1="200" y1="145" x2="250" y2="195" stroke={lineColor} strokeWidth="1.5" opacity="0.4" />
                <line x1="130" y1="100" x2="270" y2="100" stroke={lineColor} strokeWidth="1" opacity="0.2" />
                <line x1="130" y1="100" x2="150" y2="195" stroke={lineColor} strokeWidth="1" opacity="0.2" />
                <line x1="270" y1="100" x2="250" y2="195" stroke={lineColor} strokeWidth="1" opacity="0.2" />
                <line x1="150" y1="195" x2="250" y2="195" stroke={lineColor} strokeWidth="1" opacity="0.2" />
                <g className="ci-n1">
                    <circle cx="200" cy="145" r="26" fill={outerRing} opacity="0.5" />
                    <circle cx="200" cy="145" r="20" fill={nodeMain} />
                    <circle cx="200" cy="133" r="7" fill={nodeHighlight} />
                    <path d="M185 155 Q185 148 200 148 Q215 148 215 155" fill={nodeHighlight} />
                </g>
                <g className="ci-n2">
                    <circle cx="130" cy="100" r="18" fill={nodeOther} opacity="0.85" />
                    <circle cx="130" cy="90" r="6" fill={nodeHighlight} />
                    <path d="M118 108 Q118 102 130 102 Q142 102 142 108" fill={nodeHighlight} />
                </g>
                <g className="ci-n3">
                    <circle cx="270" cy="100" r="18" fill={nodeOther} opacity="0.85" />
                    <circle cx="270" cy="90" r="6" fill={nodeHighlight} />
                    <path d="M258 108 Q258 102 270 102 Q282 102 282 108" fill={nodeHighlight} />
                </g>
                <g className="ci-n4">
                    <circle cx="150" cy="195" r="18" fill={nodeOther} opacity="0.85" />
                    <circle cx="150" cy="185" r="6" fill={nodeHighlight} />
                    <path d="M138 203 Q138 197 150 197 Q162 197 162 203" fill={nodeHighlight} />
                </g>
                <g className="ci-n5">
                    <circle cx="250" cy="195" r="18" fill={nodeOther} opacity="0.85" />
                    <circle cx="250" cy="185" r="6" fill={nodeHighlight} />
                    <path d="M238 203 Q238 197 250 197 Q262 197 262 203" fill={nodeHighlight} />
                </g>
                <rect x="153" y="228" width="94" height="20" rx="10" fill={badgeBg} />
                <text x="200" y="242" textAnchor="middle" fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize="9.5" fontWeight="700" fill="white">Network</text>
            </svg>
        </CardIllustration>
    );
};

export const IllustrationCareerPath = () => {
    const { isDark } = useTheme();
    const pathColor = isDark ? '#a78bfa' : '#9087D8';
    const youBg = isDark ? '#374151' : '#DDDAF8';
    const youBorder = isDark ? '#6d28d9' : '#9087D8';
    const youText = isDark ? '#c4b5fd' : '#3A31A0';
    const job1Bg = isDark ? '#4c1d95' : '#B8B2F0';
    const job1Border = isDark ? '#7c3aed' : '#7068CC';
    const job1Text = isDark ? '#e9d5ff' : '#1E1870';
    const dreamColor1 = isDark ? '#7c3aed' : '#7B6DE8';
    const dreamColor2 = isDark ? '#5b21b6' : '#4A3CA8';
    const arrowColor = isDark ? '#8b5cf6' : '#6B5EC8';
    const sparkle = isDark ? '#a78bfa' : '#B8B2F0';
    return (
        <CardIllustration bgGradient={isDark ? 'linear-gradient(135deg, #111, #110e18)' : 'radial-gradient(circle, #F5F4FE, #ECEAFE)'}>
            <svg width="100%" height="100%" viewBox="20 50 360 200" preserveAspectRatio="xMidYMid meet">
                <defs>
                    <radialGradient id="ciDreamGrad" cx="40%" cy="35%" r="60%">
                        <stop offset="0%" stopColor={dreamColor1} />
                        <stop offset="100%" stopColor={dreamColor2} />
                    </radialGradient>
                    <style>{`
                        @keyframes starP{0%,100%{opacity:1}50%{opacity:.4}}
                        .ci-star{animation:starP 2s ease-in-out infinite}
                    `}</style>
                </defs>
                <path d="M 72 210 Q 150 170 200 148 Q 250 126 310 85" fill="none" stroke={pathColor} strokeWidth="2" strokeDasharray="6 4" opacity="0.6" />
                <polygon points="305,78 318,88 308,96" fill={arrowColor} opacity="0.7" />
                <circle cx="72" cy="210" r="24" fill={youBg} stroke={youBorder} strokeWidth="1.5" />
                <text x="72" y="207" textAnchor="middle" fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize="9" fontWeight="600" fill={youText}>You</text>
                <text x="72" y="219" textAnchor="middle" fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize="7.5" fill={youText} opacity="0.8">now</text>
                <circle cx="200" cy="148" r="26" fill={job1Bg} stroke={job1Border} strokeWidth="1.5" />
                <text x="200" y="144" textAnchor="middle" fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize="9" fontWeight="600" fill={job1Text}>Job 1</text>
                <text x="200" y="156" textAnchor="middle" fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize="7.5" fill={job1Text} opacity="0.8">1st move</text>
                <circle cx="315" cy="85" r="32" fill="url(#ciDreamGrad)" stroke={dreamColor2} strokeWidth="1.5" />
                <text x="315" y="81" textAnchor="middle" fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize="10" fontWeight="700" fill="white">Dream</text>
                <text x="315" y="94" textAnchor="middle" fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize="8" fill="#e9d5ff">job</text>
                <circle className="ci-star" cx="350" cy="58" r="3" fill={sparkle} />
                <circle className="ci-star" cx="280" cy="55" r="2" fill={sparkle} style={{ animationDelay: '0.6s' }} />
                <circle className="ci-star" cx="355" cy="100" r="2.5" fill={sparkle} style={{ animationDelay: '1.2s' }} />
            </svg>
        </CardIllustration>
    );
};

export const IllustrationTenure = () => {
    const { isDark } = useTheme();
    const bar1 = isDark ? '#D97706' : '#F5C060';
    const bar1b = isDark ? '#b45309' : '#E8A835';
    const bar2 = isDark ? '#d97706' : '#EFA030';
    const bar2b = isDark ? '#92400e' : '#D88818';
    const bar3 = isDark ? '#b45309' : '#E08520';
    const bar3b = isDark ? '#78350f' : '#C07010';
    const bar4 = isDark ? '#92400e' : '#C86810';
    const bar4b = isDark ? '#451a03' : '#A05008';
    const baseline = isDark ? 'rgba(255,255,255,0.12)' : '#D49030';
    const gridLine = isDark ? 'rgba(255,255,255,0.05)' : '#D49030';
    const xLabel = isDark ? 'rgba(255,255,255,0.45)' : '#8A5A10';
    const xLabelBold = isDark ? 'rgba(255,255,255,0.7)' : '#5C3808';
    const goodBg = isDark ? '#059669' : '#E8F6E8';
    const goodStroke = isDark ? '#10B981' : '#4A9A4A';
    const goodArrow = isDark ? '#34D399' : '#2E7A2E';
    return (
        <CardIllustration bgGradient={isDark ? 'linear-gradient(135deg, #111, #15110a)' : 'radial-gradient(circle, #FFFAF3, #FEF2DC)'}>
            <svg width="100%" height="100%" viewBox="40 50 340 200" preserveAspectRatio="xMidYMid meet">
                <defs>
                    <linearGradient id="ciBg1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={bar1} /><stop offset="100%" stopColor={bar1b} /></linearGradient>
                    <linearGradient id="ciBg2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={bar2} /><stop offset="100%" stopColor={bar2b} /></linearGradient>
                    <linearGradient id="ciBg3" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={bar3} /><stop offset="100%" stopColor={bar3b} /></linearGradient>
                    <linearGradient id="ciBg4" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={bar4} /><stop offset="100%" stopColor={bar4b} /></linearGradient>
                    <style>{`
                        @keyframes ciGrow{from{transform:scaleY(0)}to{transform:scaleY(1)}}
                        .ci-b1{animation:ciGrow .6s ease-out .1s both;transform-origin:50% 100%}
                        .ci-b2{animation:ciGrow .6s ease-out .3s both;transform-origin:50% 100%}
                        .ci-b3{animation:ciGrow .6s ease-out .5s both;transform-origin:50% 100%}
                        .ci-b4{animation:ciGrow .6s ease-out .7s both;transform-origin:50% 100%}
                    `}</style>
                </defs>
                <line x1="65" y1="210" x2="320" y2="210" stroke={baseline} strokeWidth="1" opacity="0.4" />
                <line x1="65" y1="175" x2="320" y2="175" stroke={gridLine} strokeWidth="0.5" strokeDasharray="3 3" opacity="0.25" />
                <line x1="65" y1="140" x2="320" y2="140" stroke={gridLine} strokeWidth="0.5" strokeDasharray="3 3" opacity="0.25" />
                <line x1="65" y1="105" x2="320" y2="105" stroke={gridLine} strokeWidth="0.5" strokeDasharray="3 3" opacity="0.25" />
                <g className="ci-b1"><rect x="80" y="188" width="40" height="22" rx="4" fill="url(#ciBg1)" /></g>
                <g className="ci-b2"><rect x="140" y="162" width="40" height="48" rx="4" fill="url(#ciBg2)" /></g>
                <g className="ci-b3"><rect x="200" y="128" width="40" height="82" rx="4" fill="url(#ciBg3)" /></g>
                <g className="ci-b4"><rect x="260" y="88" width="40" height="122" rx="4" fill="url(#ciBg4)" /></g>
                <text x="100" y="227" textAnchor="middle" fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize="9" fill={xLabel}>6mo</text>
                <text x="160" y="227" textAnchor="middle" fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize="9" fill={xLabel}>1yr</text>
                <text x="220" y="227" textAnchor="middle" fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize="9" fill={xLabel}>1.5yr</text>
                <text x="280" y="227" textAnchor="middle" fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize="9" fontWeight="700" fill={xLabelBold}>2yr+</text>
                <g opacity="0.85">
                    <circle cx="340" cy="120" r="16" fill={goodBg} stroke={goodStroke} strokeWidth="1" />
                    <polyline points="333,124 340,113 347,124" fill="none" stroke={goodArrow} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                    <line x1="340" y1="113" x2="340" y2="128" stroke={goodArrow} strokeWidth="2.2" strokeLinecap="round" />
                </g>
            </svg>
        </CardIllustration>
    );
};

export const IllustrationRoleFunnel = ({ roleLabel }) => {
    const { isDark } = useTheme();
    const topFill = isDark ? '#1e3a5f' : '#A8DFC8';
    const topFill2 = isDark ? '#1e4068' : '#60C4A0';
    const midFill = isDark ? '#2563eb' : '#38B08A';
    const midFill2 = isDark ? '#1d4ed8' : '#1A9070';
    const botFill = isDark ? '#1e40af' : '#108860';
    const botFill2 = isDark ? '#1e3a8a' : '#0A6848';
    const outlineColor = isDark ? '#3b82f6' : '#0A7058';
    const topText = isDark ? '#93c5fd' : '#0A5040';
    const midText = isDark ? 'rgba(255,255,255,0.6)' : '#084038';
    const botText = isDark ? '#fff' : '#E8FFF8';
    const dotColor = isDark ? '#60a5fa' : '#9EE8CE';
    const countColor = isDark ? '#93c5fd' : '#0A6848';
    return (
        <CardIllustration bgGradient={isDark ? 'linear-gradient(135deg, #111, #0d1220)' : 'radial-gradient(circle, #F0FAF7, #E0F5EE)'}>
            <svg width="100%" height="100%" viewBox="60 30 280 220" preserveAspectRatio="xMidYMid meet">
                <defs>
                    <linearGradient id="ciFT" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={topFill} /><stop offset="100%" stopColor={topFill2} /></linearGradient>
                    <linearGradient id="ciFM" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={midFill} /><stop offset="100%" stopColor={midFill2} /></linearGradient>
                    <linearGradient id="ciFB" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={botFill} /><stop offset="100%" stopColor={botFill2} /></linearGradient>
                    <style>{`
                        @keyframes ciDrop{0%{opacity:0;transform:translateY(-8px)}20%{opacity:1;transform:translateY(0)}80%{opacity:1;transform:translateY(0)}100%{opacity:0;transform:translateY(6px)}}
                        .ci-d1{animation:ciDrop 2.4s ease-in-out infinite 0s}
                        .ci-d2{animation:ciDrop 2.4s ease-in-out infinite .4s}
                        .ci-d3{animation:ciDrop 2.4s ease-in-out infinite .8s}
                    `}</style>
                </defs>
                <rect x="98" y="50" width="204" height="32" rx="5" fill="url(#ciFT)" />
                <polygon points="98,82 302,82 252,128 148,128" fill="url(#ciFM)" />
                <rect x="148" y="128" width="104" height="34" fill="url(#ciFM)" />
                <rect x="170" y="162" width="60" height="28" rx="5" fill="url(#ciFB)" />
                <path d="M98 53 L302 53 L302 82 L252 128 L252 162 L230 162 L230 190 L170 190 L170 162 L148 162 L148 128 L98 82 Z" fill="none" stroke={outlineColor} strokeWidth="1" opacity="0.3" />
                <text x="200" y="72" textAnchor="middle" fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize="10" fontWeight="600" fill={topText}>All companies (129)</text>
                <text x="200" y="114" textAnchor="middle" fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize="9" fill={midText} opacity="0.9">Filter by your role</text>
                <text x="200" y="152" textAnchor="middle" fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize="9" fill={botText} fontWeight="600">{roleLabel || 'PM'} hires only</text>
                <circle className="ci-d1" cx="178" cy="198" r="3.5" fill={dotColor} opacity="0.8" />
                <circle className="ci-d2" cx="196" cy="198" r="3.5" fill={dotColor} opacity="0.8" />
                <circle className="ci-d3" cx="214" cy="198" r="3.5" fill={dotColor} opacity="0.8" />
                <text x="200" y="220" textAnchor="middle" fontFamily="'Helvetica Neue',Arial,sans-serif" fontSize="10" fontWeight="700" fill={countColor}>18 companies</text>
            </svg>
        </CardIllustration>
    );
};

export const IllustrationLocation = () => {
    const { isDark } = useTheme();
    const gridColor = isDark ? 'rgba(255,255,255,0.06)' : '#A8C8E8';
    const ringStroke = isDark ? '#0ea5e9' : '#3A88D0';
    const pinColor1 = isDark ? '#0ea5e9' : '#4A9FE0';
    const pinColor2 = isDark ? '#0284c7' : '#1A6BB8';
    const dotLarge = isDark ? '#1e3a5f' : '#85B7EB';
    const dotSmall = isDark ? '#1e293b' : '#B5D4F4';
    const dotTiny = isDark ? '#1e293b' : '#C8DFF8';
    const shadowColor = isDark ? 'rgba(14,165,233,0.15)' : '#8AC4F0';
    return (
        <CardIllustration bgGradient={isDark ? 'linear-gradient(135deg, #111, #0d1018)' : 'radial-gradient(circle, #F2F8FE, #E2F0FC)'}>
            <svg width="100%" height="100%" viewBox="40 30 320 210" preserveAspectRatio="xMidYMid meet">
                <defs>
                    <radialGradient id="ciPinGrad" cx="40%" cy="35%" r="60%">
                        <stop offset="0%" stopColor={pinColor1} />
                        <stop offset="100%" stopColor={pinColor2} />
                    </radialGradient>
                    <linearGradient id="ciShadow" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={shadowColor} stopOpacity="0.4" />
                        <stop offset="100%" stopColor={shadowColor} stopOpacity="0" />
                    </linearGradient>
                    <style>{`
                        @keyframes ciRing{0%{r:8;opacity:.5}100%{r:65;opacity:0}}
                        @keyframes ciBounce{0%,100%{transform:translateY(0)}50%{transform:translateY(-5px)}}
                        .ci-rng{animation:ciRing 2.6s ease-out infinite}
                        .ci-rng2{animation:ciRing 2.6s ease-out infinite .9s}
                        .ci-pin{animation:ciBounce 3s ease-in-out infinite;transform-origin:200px 135px}
                    `}</style>
                </defs>
                {[60, 90, 120, 150, 180, 210].map(y => <line key={`h${y}`} x1="60" y1={y} x2="340" y2={y} stroke={gridColor} strokeWidth="0.4" opacity="0.5" />)}
                {[80, 130, 180, 230, 280, 330].map(x => <line key={`v${x}`} x1={x} y1="40" x2={x} y2="220" stroke={gridColor} strokeWidth="0.4" opacity="0.5" />)}
                <circle className="ci-rng" cx="200" cy="150" r="8" fill="none" stroke={ringStroke} strokeWidth="1.5" />
                <circle className="ci-rng2" cx="200" cy="150" r="8" fill="none" stroke={ringStroke} strokeWidth="1.5" />
                <ellipse cx="200" cy="208" rx="24" ry="8" fill="url(#ciShadow)" />
                <circle cx="140" cy="112" r="5" fill={dotLarge} opacity="0.7" />
                <circle cx="265" cy="100" r="5" fill={dotLarge} opacity="0.7" />
                <circle cx="120" cy="168" r="4.5" fill={dotSmall} opacity="0.6" />
                <circle cx="290" cy="165" r="4.5" fill={dotSmall} opacity="0.6" />
                <circle cx="255" cy="180" r="4" fill={dotSmall} opacity="0.6" />
                <circle cx="155" cy="76" r="3.5" fill={dotTiny} opacity="0.5" />
                <g className="ci-pin">
                    <path d="M200 88 C183 88 170 101 170 117 C170 140 200 170 200 170 C200 170 230 140 230 117 C230 101 217 88 200 88 Z" fill="url(#ciPinGrad)" />
                    <circle cx="200" cy="114" r="10" fill="white" opacity="0.95" />
                    <circle cx="200" cy="114" r="5.5" fill={pinColor2} />
                    <circle cx="197" cy="111" r="2.2" fill="white" opacity="0.6" />
                </g>
            </svg>
        </CardIllustration>
    );
};
